import React, { useRef, useEffect } from "react"
import { Script } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { gtmTracking } from "@/utils/gtm-tracking"

const getFullUrl = url => {
  if (!url || typeof window === "undefined") return url
  return url.startsWith("http") ? url : `${window.location.origin}${url}`
}

const isStringPosterImage = posterImage => {
  return typeof posterImage === "string"
}

const WistiaVideoEmbedPopupVideo = ({ videoId, popup = false, stillUrl }) => {
  useEffect(() => {
    if (stillUrl && typeof window !== "undefined") {
      window._wq = window._wq || []
      window._wq.push({
        id: videoId,
        stillUrl: getFullUrl(stillUrl),
      })
    }
  }, [videoId, stillUrl])

  return (
    <>
      <Script
        src="https://fast.wistia.net/assets/external/E-v1.js"
        async={true}
      />

      <div
        id={`wistia-video-embed-${videoId}`}
        className={`wistia_embed wistia_async_${videoId} ${
          popup ? "popover=true" : ""
        }`}
        style={{
          height: "100%",
          left: 0,
          position: "absolute",
          top: 0,
          width: "100%",
        }}
      >
        &nbsp;
      </div>
    </>
  )
}

const WistiaVideoEmbedVideo = ({ videoId, thumbnail, className = "" }) => {
  const swatchRef = useRef(null)

  return (
    <>
      <Script
        src="https://fast.wistia.com/assets/external/E-v1.js"
        async={true}
      />

      <div
        className={`wistia_embed_responsive_padding ${className}`}
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <div
          className="wistia_embed__responsive_wrapper"
          style={{
            height: "100%",
            left: 0,
            position: "relative",
            top: 0,
            width: "100%",
          }}
        >
          <div
            className={`wistia_embed wistia_async_${videoId} videoFoam=true`}
            style={{
              height: "100%",
              position: "relative",
              width: "100%",
            }}
          >
            <div
              className="wistia_swatch"
              ref={swatchRef}
              style={{
                height: "100%",
                left: 0,
                opacity: 0,
                overflow: "hidden",
                position: "absolute",
                top: 0,
                transition: "opacity 200ms",
                width: "100%",
              }}
            >
              <img
                src={thumbnail}
                style={{
                  filter: "blur(5px)",
                  height: "100%",
                  objectFit: "contain",
                  width: "100%",
                }}
                alt=""
                role="presentation"
                aria-hidden="true"
                onLoad={() => {
                  if (swatchRef.current) {
                    swatchRef.current.style.opacity = 1
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const WistiaVideoEmbed = ({
  videoId,
  videoStyle,
  videoType,
  posterImage,
  showPlayButton = "true",
  className = "",
  replaceThumbnail = false,
}) => {
  const isPopup = videoType === "popup"
  const showPlayButtonIcon = showPlayButton === "true"
  const hasCustomThumbnail =
    replaceThumbnail && isStringPosterImage(posterImage)
  const stillUrl = hasCustomThumbnail ? posterImage : null

  const handleClickPlayButton = () => {
    gtmTracking("Video Play", videoId, "Wistia Video Embed")()
    window._wq.push({
      id: videoId,
      onReady(video) {
        video.play()
      },
    })
  }

  const renderPosterImage = () => {
    if (!posterImage) return null

    if (isStringPosterImage(posterImage)) {
      return <img src={posterImage} alt="" role="presentation" />
    }

    return (
      <GatsbyImage
        image={getImage(posterImage.src)}
        alt={posterImage.alt}
        className="wistia-video-embed__overlay"
      />
    )
  }

  if (!videoId) {
    return <h2 className="h4">No Wistia Video ID Found</h2>
  }

  if (videoStyle === "branded") {
    const containerClassName = [
      "wistia-video-embed",
      className,
      replaceThumbnail && "wistia-video-embed--custom-thumbnail",
    ]
      .filter(Boolean)
      .join(" ")

    return (
      <div className={containerClassName} data-wistia-id={videoId}>
        <button
          className="wistia-video-embed__overlay"
          onClick={handleClickPlayButton}
        >
          {renderPosterImage()}

          {showPlayButtonIcon && (
            <svg
              className="wistia-video-embed__play-button"
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="40" cy="40" r="40" fill="#453DD8" />
              <path
                d="M55.2253 38.3762C56.7052 39.3196 56.7052 41.4803 55.2253 42.4237L33.29 56.4073C31.6924 57.4258 29.5999 56.2782 29.5999 54.3835L29.5999 26.4164C29.5999 24.5217 31.6924 23.3741 33.29 24.3926L55.2253 38.3762Z"
                fill="white"
              />
            </svg>
          )}

          <WistiaVideoEmbedPopupVideo
            videoId={videoId}
            popup={isPopup}
            stillUrl={stillUrl}
          />
        </button>
      </div>
    )
  }

  return (
    <WistiaVideoEmbedVideo
      videoId={videoId}
      thumbnail={posterImage}
      className={className}
    />
  )
}

export default WistiaVideoEmbed
